import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { ExpenseTransInfoService, BaseDataInfo, BaseInfoLabel } from './expense-trans-info.service';

@Component({
    selector: 'gtw-expense-trans-info',
    templateUrl: './expense-trans-info.component.html',
    styleUrls: ['./expense-trans-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ExpenseTransInfoService],
    standalone: false
})
export class ExpenseTransInfoComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  // ============================================
  // OUTPUTS
  // ============================================
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ============================================
  // CONTEXT (was @Input bindings)
  // ============================================

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = { USER_SETTINGS: '', SERVER_CONFIG: '' };
  GENERAL_CONFIG: any;

  rowData: any = {};

  colData: any = {};

  label: BaseInfoLabel | null = null;

  // ============================================
  // COMPONENT STATE
  // ============================================

  isLoading = true;
  title = '';
  baseDataInfo: BaseDataInfo[] = [];

  private initComplete = false;

  // ============================================
  // CONSTRUCTOR
  // ============================================

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: ExpenseTransInfoService,
    private cdr: ChangeDetectorRef
  ) {}

  // ============================================
  // LIFECYCLE
  // ============================================

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.initComplete = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = { ...header };
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.colData = activeScreen?.data?.colData || activeScreen?.data?.col_data || {};
    this.extractLabelFromColData();
    const lbl = activeScreen?.data?.label || '';
    if (lbl === 'Base Basket' || lbl === 'Base Type' || lbl === 'Base FDII') {
      this.label = lbl as BaseInfoLabel;
      this.title = lbl;
    }
    this.service.setGlobalParams(header.globalParams || {});
    this.service.setFilterParams(this.filterParams);
  }

  /**
   * Extract label from colData
   * This logic was previously in AngularJS controller (vm.label = gridData.colData.label)
   * Now it's handled entirely within the Angular component
   */
  private extractLabelFromColData(): void {
    if (this.colData && this.colData.label) {
      const label = this.colData.label;
      if (label === 'Base Basket' || label === 'Base Type' || label === 'Base FDII') {
        this.label = label as BaseInfoLabel;
        this.title = label;
      }
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  private init(): void {
    // Check if we have all required data
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData?.TRANS_CATG_KEY || !this.label) {
      return;
    }

    if (this.initComplete) {
      return;
    }

    this.initComplete = true;
    this.loadData();
  }

  private loadData(): void {
    if (!this.label) {
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const transCatgKey = this.rowData.TRANS_CATG_KEY;
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;

    this.isLoading = true;
    this.cdr.detectChanges();

    this.service.getBaseDataInfo(baseUrl, this.label, transCatgKey, ssoId, taxYear, scenario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: BaseDataInfo[]) => {
        this.baseDataInfo = data;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading base data info:', error);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ============================================
  // ACTIONS
  // ============================================

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
